// Onboarding screens on the login page (backend onboarding.py,
// routers/onboarding.py): "Request access" (self-registration, approved by
// an admin), invite links (/#invite=<token>, pre-approved by an admin), and
// the shared password-strength + two-factor (TOTP) setup pieces. The invite
// token lives in the URL fragment, so it is never sent to the server in a
// request line or a Referer; it is posted only to the accept route.
import { ApiError, getJson, postJson } from "./api";
import { el } from "./legend";
import type { CurrentUser } from "./state";

const PASSWORD_MIN = 12;
const PASSWORD_NOTE = "Your password is hashed before it is stored. Nobody, including administrators, can see it.";
const MFA_NOTE =
  "Two-factor sign-in asks for a 6-digit code from an app on your phone as well as your password, so a stolen password alone can't open your account.";

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 429) return "Too many attempts. Please wait a while and try again.";
    if (typeof e.detail === "string") return e.detail;
    if (e.status === 422) return "Please check the form and try again.";
  }
  return "Something went wrong. Please try again.";
}

function field(labelText: string, input: HTMLElement, hint?: string): HTMLElement {
  const wrap = el("div", { className: "onb-field" });
  const label = el("label", {}, labelText);
  wrap.append(label, input);
  if (hint) wrap.append(el("div", { className: "caption onb-hint" }, hint));
  return wrap;
}

function input(type: string, autocomplete: string, extra: Partial<HTMLInputElement> = {}): HTMLInputElement {
  const node = el("input", { type, ...extra }) as HTMLInputElement;
  node.setAttribute("autocomplete", autocomplete);
  return node;
}

// ---------------------------------------------------------------------------
// Password + strength
// ---------------------------------------------------------------------------

export function passwordStrength(pw: string, username = ""): { ok: boolean; level: 0 | 1 | 2 | 3; text: string } {
  if (pw.length < PASSWORD_MIN) {
    return { ok: false, level: 0, text: `At least ${PASSWORD_MIN} characters (${pw.length}/${PASSWORD_MIN})` };
  }
  if (username && pw.toLowerCase() === username.toLowerCase()) {
    return { ok: false, level: 0, text: "Can't be the same as your username" };
  }
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length;
  const unique = new Set(pw).size;
  if (unique < 5) return { ok: true, level: 1, text: "Weak: too many repeated characters" };
  if (pw.length >= 20 || (pw.length >= 16 && classes >= 3)) return { ok: true, level: 3, text: "Strong" };
  if (pw.length >= 14 || classes >= 3) return { ok: true, level: 2, text: "Good" };
  return { ok: true, level: 1, text: "OK. Longer is stronger: a short phrase works well" };
}

interface PasswordFields {
  el: HTMLElement;
  value: () => string;
  /** null when fine, else the problem to show */
  problem: () => string | null;
}

function passwordFields(username: () => string): PasswordFields {
  const pw = input("password", "new-password", { required: true, minLength: PASSWORD_MIN, maxLength: 200 });
  const confirm = input("password", "new-password", { required: true, maxLength: 200 });
  const bar = el("div", { className: "onb-strength" });
  const fill = el("div", { className: "onb-strength-fill" });
  bar.append(fill);
  const text = el("div", { className: "caption onb-strength-text" });
  const show = el("label", { className: "caption onb-show" });
  const showBox = input("checkbox", "off");
  show.append(showBox, document.createTextNode(" Show password"));
  showBox.addEventListener("change", () => {
    pw.type = confirm.type = showBox.checked ? "text" : "password";
  });
  const update = () => {
    const s = passwordStrength(pw.value, username());
    fill.style.width = `${pw.value ? [15, 40, 70, 100][s.level] : 0}%`;
    fill.dataset.level = String(s.level);
    text.textContent = pw.value ? s.text : `At least ${PASSWORD_MIN} characters. A short phrase of a few words is easy to remember.`;
  };
  pw.addEventListener("input", update);
  update();
  const wrap = el("div");
  wrap.append(
    field("Choose a password", pw),
    bar,
    text,
    field("Confirm password", confirm),
    show,
    el("p", { className: "caption onb-note" }, `🔒 ${PASSWORD_NOTE}`),
  );
  return {
    el: wrap,
    value: () => pw.value,
    problem: () => {
      const s = passwordStrength(pw.value, username());
      if (!s.ok) return s.text;
      if (pw.value !== confirm.value) return "The two passwords don't match";
      return null;
    },
  };
}

// ---------------------------------------------------------------------------
// Two-factor (TOTP) setup
// ---------------------------------------------------------------------------

interface MfaSetup {
  el: HTMLElement;
  /** {secret, code} once set up, else null */
  value: () => { secret: string; code: string } | null;
  started: () => boolean;
}

/** The server-rendered QR code (onboarding.qr_svg, an otpauth:// URI) as a
 * DOM node: parsed as SVG, never injected as HTML. */
export function qrNode(svg: string | undefined): HTMLElement | null {
  if (!svg) return null;
  const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
  const root = doc.documentElement;
  if (!root || root.nodeName.toLowerCase() !== "svg" || doc.querySelector("parsererror, script, foreignObject")) return null;
  const wrap = el("div", { className: "onb-qr" });
  root.setAttribute("role", "img");
  root.setAttribute("aria-label", "QR code for your authenticator app");
  wrap.append(document.importNode(root, true));
  return wrap;
}

function mfaSetup(account: () => string): MfaSetup {
  const wrap = el("div", { className: "onb-mfa" });
  wrap.append(el("h3", {}, "Two-factor sign-in"), el("p", { className: "caption" }, MFA_NOTE));
  let secret: string | null = null;
  const codeInput = input("text", "one-time-code", { inputMode: "numeric", maxLength: 7, placeholder: "123456" });
  const startBtn = el("button", { type: "button", className: "onb-secondary" }, "Set up two-factor sign-in");
  const body = el("div");
  startBtn.addEventListener("click", () => {
    startBtn.disabled = true;
    void postJson<{ secret: string; otpauth_uri: string; qr_svg?: string }>("/api/auth/mfa/new-secret", { account: account() })
      .then((s) => {
        secret = s.secret;
        startBtn.remove();
        const grouped = s.secret.replace(/(.{4})/g, "$1 ").trim();
        const key = el("code", { className: "onb-secret" }, grouped);
        const copy = el("button", { type: "button", className: "onb-secondary" }, "Copy key");
        copy.addEventListener("click", () => {
          void navigator.clipboard?.writeText(s.secret).then(() => (copy.textContent = "Copied"));
        });
        const link = el("a", { href: s.otpauth_uri }, "open it in your authenticator app");
        const steps = el("ol", { className: "caption onb-steps" });
        const li1 = el("li", {}, "Install an authenticator app on your phone (Microsoft Authenticator, Google Authenticator, 1Password, ...).");
        const li2 = el("li");
        li2.append(
          document.createTextNode("Scan this QR code with the app, or add an account using the setup key below (on this phone you can also "),
          link,
          document.createTextNode("):"),
        );
        const qr = qrNode(s.qr_svg);
        if (qr) li2.append(qr);
        const keyRow = el("div", { className: "onb-keyrow" });
        keyRow.append(key, copy);
        li2.append(keyRow);
        const li3 = el("li", {}, "Type the 6-digit code the app shows:");
        li3.append(codeInput);
        steps.append(li1, li2, li3);
        body.append(steps);
        codeInput.focus();
      })
      .catch((e) => {
        startBtn.disabled = false;
        body.textContent = errorText(e);
      });
  });
  wrap.append(startBtn, body);
  return {
    el: wrap,
    started: () => secret !== null,
    value: () => (secret ? { secret, code: codeInput.value.replace(/\s/g, "") } : null),
  };
}

// ---------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------

function panel(title: string, intro?: string): { root: HTMLElement; body: HTMLElement; error: HTMLElement } {
  const root = el("div", { className: "onb-panel" });
  root.append(el("h1", {}, title));
  if (intro) root.append(el("p", { className: "caption" }, intro));
  const body = el("div", { className: "onb-body" });
  const error = el("div", { className: "caption login-error", role: "alert" } as Partial<HTMLDivElement>);
  root.append(body, error);
  return { root, body, error };
}

function showPanel(node: HTMLElement): void {
  const screen = document.getElementById("login-screen")!;
  const form = document.getElementById("login-form") as HTMLElement;
  screen.querySelectorAll(".onb-panel").forEach((n) => n.remove());
  form.style.display = "none";
  screen.style.display = "flex";
  screen.append(node);
}

function backToLogin(): void {
  const screen = document.getElementById("login-screen")!;
  screen.querySelectorAll(".onb-panel").forEach((n) => n.remove());
  (document.getElementById("login-form") as HTMLElement).style.display = "";
}

function backLink(): HTMLElement {
  const a = el("a", { href: "#", className: "caption" }, "Back to sign in");
  a.addEventListener("click", (e) => {
    e.preventDefault();
    backToLogin();
  });
  return a;
}

export function showRequestAccess(): void {
  const p = panel(
    "Request access to WRAP",
    "Pick your own username and password. An administrator reviews each request; you can sign in once it is approved.",
  );
  const name = input("text", "name", { required: true, maxLength: 120 });
  const email = input("email", "email", { maxLength: 200 });
  const org = input("text", "organization", { required: true, maxLength: 200 });
  const reason = el("textarea", { rows: 2, maxLength: 2000 }) as HTMLTextAreaElement;
  const username = input("text", "username", { required: true, maxLength: 64 });
  const pw = passwordFields(() => username.value);
  const mfa = mfaSetup(() => username.value);
  // honeypot: hidden from people (and screen readers); bots fill it in
  const trap = input("text", "off", { name: "website", tabIndex: -1 });
  const trapWrap = el("div", { className: "onb-trap" });
  trapWrap.setAttribute("aria-hidden", "true");
  trapWrap.append(el("label", {}, "Website"), trap);
  const submit = el("button", { type: "submit" }, "Send request");
  const form = el("form", { className: "onb-form" });
  form.append(
    field("Your name", name),
    field("Email (optional)", email, "So the administrator can contact you."),
    field("Organisation", org),
    field("Why do you need access? (optional)", reason),
    field("Username", username, "3-64 letters, numbers, dots, dashes or underscores."),
    pw.el,
    mfa.el,
    trapWrap,
    submit,
    backLink(),
  );
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    p.error.textContent = "";
    const problem = pw.problem();
    if (problem) {
      p.error.textContent = problem;
      return;
    }
    const m = mfa.value();
    if (mfa.started() && !/^\d{6}$/.test(m?.code ?? "")) {
      p.error.textContent = "Enter the 6-digit code from your authenticator app (or reload to skip two-factor).";
      return;
    }
    submit.disabled = true;
    postJson<{ message: string }>("/api/auth/register", {
      username: username.value.trim(),
      password: pw.value(),
      display_name: name.value.trim(),
      email: email.value.trim() || null,
      organisation: org.value.trim(),
      reason: reason.value.trim() || null,
      totp_secret: m?.secret ?? null,
      totp_code: m?.code ?? null,
      website: trap.value,
    })
      .then((r) => {
        p.body.innerHTML = "";
        p.body.append(el("p", {}, r.message), backLink());
      })
      .catch((err) => {
        p.error.textContent = errorText(err);
      })
      .finally(() => (submit.disabled = false));
  });
  p.body.append(form);
  showPanel(p.root);
  name.focus();
}

interface InviteInfo {
  organisation: string;
  label: string | null;
  email: string | null;
  role: string;
  teams: string[];
  read_only: boolean;
  expires_at: string;
}

const ROLE_TEXT: Record<string, string> = {
  view: "viewer",
  configure: "analyst",
  manager: "manager",
  admin: "administrator",
};

export function inviteTokenFromUrl(): string | null {
  const m = /[#&]invite=([A-Za-z0-9_-]{20,200})/.exec(window.location.hash);
  return m ? m[1] : null;
}

function clearInviteFromUrl(): void {
  history.replaceState(null, "", window.location.pathname + window.location.search);
}

/** "Already have a WRAP account?": use the invite to add its organisation to
 * an existing account (POST /api/auth/invite/{token}/join, memberships.py).
 * The home organisation is unchanged; the new one shows in the Organisation
 * dropdown with the invite's role and teams. */
function joinExistingForm(
  token: string,
  organisation: string,
  p: { root: HTMLElement; error: HTMLElement },
  onSignedIn: (user: CurrentUser) => void,
): HTMLElement {
  const box = el("details", { className: "onb-join" });
  box.append(el("summary", {}, `Already have a WRAP account? Add ${organisation} to it`));
  const username = input("text", "username", { required: true, maxLength: 64 });
  const password = input("password", "current-password", { required: true, maxLength: 200 });
  const code = input("text", "one-time-code", { maxLength: 16, placeholder: "123456" });
  const codeField = field("Code from your authenticator app (or a recovery code)", code);
  codeField.style.display = "none";
  const submit = el("button", { type: "submit" }, `Sign in and add ${organisation}`);
  const form = el("form", { className: "onb-form" });
  form.append(field("Username", username), field("Password", password), codeField, submit);
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    p.error.textContent = "";
    submit.disabled = true;
    postJson<CurrentUser>(`/api/auth/invite/${encodeURIComponent(token)}/join`, {
      username: username.value.trim(),
      password: password.value,
      totp_code: codeField.style.display === "none" ? null : code.value.replace(/\s/g, ""),
    })
      .then((user) => {
        clearInviteFromUrl();
        document.getElementById("login-screen")!.style.display = "none";
        p.root.remove();
        onSignedIn(user);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401 && err.detail === "mfa_required") {
          codeField.style.display = "";
          code.focus();
          p.error.textContent = "Enter the code from your authenticator app.";
          return;
        }
        p.error.textContent = errorText(err);
      })
      .finally(() => (submit.disabled = false));
  });
  box.append(form);
  return box;
}

export function showInvite(token: string, onSignedIn: (user: CurrentUser) => void): void {
  const p = panel("Set up your WRAP account");
  showPanel(p.root);
  p.body.append(el("p", { className: "caption" }, "Checking your invite…"));
  getJson<InviteInfo>(`/api/auth/invite/${encodeURIComponent(token)}`)
    .then((info) => {
      p.body.innerHTML = "";
      const who = `${info.organisation}, as a ${info.read_only ? "read-only " : ""}${ROLE_TEXT[info.role] ?? info.role}`;
      p.body.append(
        el("p", {}, `You've been invited to WRAP (${who}). Pick your own username and password below; this link works once and expires ${new Date(info.expires_at).toLocaleDateString()}.`),
      );
      const name = input("text", "name", { required: true, maxLength: 120 });
      const email = input("email", "email", { maxLength: 200, value: info.email ?? "" });
      const username = input("text", "username", { required: true, maxLength: 64 });
      const pw = passwordFields(() => username.value);
      const mfa = mfaSetup(() => username.value);
      const skipLabel = el("label", { className: "caption onb-skip" });
      const skip = input("checkbox", "off");
      skipLabel.append(skip, document.createTextNode(" Continue without two-factor sign-in (not recommended)"));
      mfa.el.append(skipLabel);
      const submit = el("button", { type: "submit" }, "Create account and sign in");
      const form = el("form", { className: "onb-form" });
      form.append(
        field("Your name", name),
        field("Email (optional)", email),
        field("Username", username, "3-64 letters, numbers, dots, dashes or underscores."),
        pw.el,
        mfa.el,
        submit,
      );
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        p.error.textContent = "";
        const problem = pw.problem();
        if (problem) {
          p.error.textContent = problem;
          return;
        }
        const m = mfa.value();
        if (!m && !skip.checked) {
          p.error.textContent = "Set up two-factor sign-in, or tick the box to continue without it.";
          return;
        }
        if (m && !/^\d{6}$/.test(m.code)) {
          p.error.textContent = "Enter the 6-digit code from your authenticator app.";
          return;
        }
        submit.disabled = true;
        postJson<CurrentUser>(`/api/auth/invite/${encodeURIComponent(token)}/accept`, {
          username: username.value.trim(),
          password: pw.value(),
          display_name: name.value.trim(),
          email: email.value.trim() || null,
          totp_secret: m?.secret ?? null,
          totp_code: m?.code ?? null,
          skip_mfa: !m && skip.checked,
        })
          .then((user) => {
            clearInviteFromUrl();
            document.getElementById("login-screen")!.style.display = "none";
            p.root.remove();
            onSignedIn(user);
          })
          .catch((err) => {
            p.error.textContent = errorText(err);
          })
          .finally(() => (submit.disabled = false));
      });
      p.body.append(form);
      p.body.append(joinExistingForm(token, info.organisation, p, onSignedIn));
      name.focus();
    })
    .catch((err) => {
      p.body.innerHTML = "";
      p.body.append(el("p", {}, errorText(err)));
      const a = el("a", { href: "#", className: "caption" }, "Go to sign in");
      a.addEventListener("click", (e) => {
        e.preventDefault();
        clearInviteFromUrl();
        backToLogin();
      });
      p.body.append(a);
    });
}
